import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { QRCodeComponent } from 'angularx-qrcode';
import { LoaderCircle, type LucideIconData } from 'lucide-angular';
import type { Subscription } from 'rxjs';

import { AuthService } from '../../../../shared/services/auth.service';
import { ButtonComponent, IconComponent } from '../../../../shared/ui';
import { ProfileQrError, ProfileQrService } from '../../data/profile-qr.service';
import { PROFILE_COPY } from '../../profile.copy';

type QrState =
  | { status: 'loading' }
  | { status: 'ready'; payload: string }
  | { status: 'clients-only' }
  | { status: 'error' };

const QR_COPY = PROFILE_COPY.COINS.QR;

/** Dónde queda el foco tras una transición de la confirmación (H3 de Astra). */
type FocusTarget = 'yes' | 'regenerate' | 'clients-only';

/**
 * «Mi QR Perrón» (A1·web, Q1-Q10): el QR firmado del cliente para enseñarlo en caja, y la opción de regenerarlo en
 * dos pasos (el anterior deja de resolver en el TPV).
 *
 * **Solo cliente (navegador).** `angularx-qrcode` no comprueba la plataforma: este componente únicamente vive en
 * `/perfil/coins`, que es `RenderMode.Client` (`app.routes.server.ts`). No lo importes en nada que se renderice en el
 * servidor.
 *
 * **El estado pertenece a la cuenta** (patrón de `ProfileOrdersComponent`): un efecto sobre `currentUser()?.id` carga
 * con el primer id y, si cambia a otro, descarta el QR pintado ANTES de pedir el nuevo, para que nunca se vea el QR de
 * otra cuenta. Sin id no se pide nada.
 *
 * El payload no se pinta como texto, no se guarda ni se registra en ningún sitio (Q9).
 */
@Component({
  selector: 'app-profile-qr',
  imports: [ButtonComponent, IconComponent, QRCodeComponent],
  templateUrl: './profile-qr.component.html',
  styleUrl: './profile-qr.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileQrComponent {
  private readonly qr = inject(ProfileQrService);
  private readonly currentUser = inject(AuthService).currentUser;
  private readonly injector = inject(Injector);

  /** Destinos de foco (H3). «Generar» es un `app-button`: se lee su host y, de ahí, el `<button>` nativo. */
  private readonly regenerateHost = viewChild<string, ElementRef<HTMLElement>>('regenerateButton', { read: ElementRef });
  private readonly yesButton = viewChild<ElementRef<HTMLButtonElement>>('yesButton');
  private readonly clientsOnlyNotice = viewChild<ElementRef<HTMLElement>>('clientsOnlyNotice');

  protected readonly copy = QR_COPY;
  protected readonly spinnerIcon: LucideIconData = LoaderCircle;

  protected readonly state = signal<QrState>({ status: 'loading' });
  /** La cadena firmada que se dibuja (solo en `ready`). Nunca se pinta como texto. */
  protected readonly qrData = computed(() => {
    const state = this.state();
    return state.status === 'ready' ? state.payload : null;
  });
  /** Paso de confirmación de «Generar un QR nuevo». */
  protected readonly confirming = signal(false);
  protected readonly regenerating = signal(false);
  /** Resultado del último regenerado, en la región `aria-live`. */
  protected readonly liveMessage = signal('');

  private request: Subscription | null = null;
  private regenerateRequest: Subscription | null = null;
  private resyncRequest: Subscription | null = null;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.cancelRequests());

    let loadedFor: number | null = null;
    effect(() => {
      const userId = this.currentUser()?.id ?? null;
      untracked(() => {
        if (userId === null) {
          // El contenedor ya quita el outlet sin sesión; por si acaso, nada en vuelo y nada pintado.
          loadedFor = null;
          this.reset();
          return;
        }
        if (userId === loadedFor) return;
        loadedFor = userId;
        this.load();
      });
    });
  }

  protected retry(): void {
    this.load();
  }

  protected askRegenerate(): void {
    this.liveMessage.set('');
    this.confirming.set(true);
    this.focusAfterRender('yes');
  }

  protected cancelRegenerate(): void {
    if (this.regenerating()) return;
    this.confirming.set(false);
    this.focusAfterRender('regenerate');
  }

  /**
   * Mientras vuela, «Sí» sigue a fuerza completa con su spinner y un segundo clic se ignora: un solo POST.
   *
   * Errores (Q4 + R1-R2 de la auditoría): 429 → el texto del throttle y el QR anterior se queda (el throttle corta antes
   * de rotar, así que sigue valiendo); 403 → estado «solo clientes», sin QR (la API ya no lo resolvería); cualquier otro
   * (500, red, payload raro) → `REGENERATE_ERROR` y una re-sincronización silenciosa con `GET /qr/me`, porque el POST
   * pudo aplicarse en el servidor y el QR pintado quizá ya no resuelve.
   */
  protected confirmRegenerate(): void {
    if (this.regenerating()) return;
    this.resyncRequest?.unsubscribe();
    this.regenerating.set(true);
    this.regenerateRequest = this.qr.regenerate().subscribe({
      next: (payload) => {
        this.state.set({ status: 'ready', payload });
        this.endRegenerate(QR_COPY.REGENERATE_DONE);
      },
      error: (err: unknown) => {
        const reason = err instanceof ProfileQrError ? err.reason : 'unknown';
        if (reason === 'rate-limited') {
          this.endRegenerate((err as ProfileQrError).message);
        } else if (reason === 'clients-only') {
          this.state.set({ status: 'clients-only' });
          this.endRegenerate('', 'clients-only');
        } else {
          this.endRegenerate(QR_COPY.REGENERATE_ERROR);
          this.resyncAfterFailedRegenerate();
        }
      },
    });
  }

  /** Cierra la confirmación; el mensaje va a la región `aria-live` y el foco, a `focus` (H3). */
  private endRegenerate(message: string, focus: FocusTarget = 'regenerate'): void {
    this.regenerating.set(false);
    this.confirming.set(false);
    this.liveMessage.set(message);
    this.focusAfterRender(focus);
  }

  /**
   * H3 de Astra: el botón con el foco desaparece en cada transición de la confirmación, y sin esto el foco caería al
   * `<body>`. Tras el siguiente render, lo lleva a «Sí, generar uno nuevo», a «Generar un QR nuevo» o al aviso de «solo
   * clientes» (párrafo con `tabindex="-1"`). Si el destino no existe (p. ej. cambió la cuenta entre medias), no hace nada.
   */
  private focusAfterRender(target: FocusTarget): void {
    afterNextRender(
      () => {
        const element =
          target === 'yes'
            ? this.yesButton()?.nativeElement
            : target === 'clients-only'
              ? this.clientsOnlyNotice()?.nativeElement
              : this.regenerateHost()?.nativeElement.querySelector('button');
        element?.focus();
      },
      { injector: this.injector },
    );
  }

  /**
   * R2: tras un regenerado fallido sin causa conocida, vuelve a leer el QR vigente SIN esqueleto ni mensaje nuevo. Si
   * responde, se pinta ESE payload (el anterior o uno nuevo); si falla, se queda el QR pintado.
   */
  private resyncAfterFailedRegenerate(): void {
    this.resyncRequest?.unsubscribe();
    this.resyncRequest = this.qr.me().subscribe({
      next: (payload) => this.state.set({ status: 'ready', payload }),
      error: () => undefined,
    });
  }

  /** Carga NO silenciosa: el QR pintado (de esta cuenta o de otra) desaparece antes de pedir el nuevo. */
  private load(): void {
    this.reset();
    this.request = this.qr.me().subscribe({
      next: (payload) => this.state.set({ status: 'ready', payload }),
      error: (err: unknown) =>
        this.state.set(
          err instanceof ProfileQrError && err.reason === 'clients-only' ? { status: 'clients-only' } : { status: 'error' },
        ),
    });
  }

  private reset(): void {
    this.cancelRequests();
    this.state.set({ status: 'loading' });
    this.confirming.set(false);
    this.regenerating.set(false);
    this.liveMessage.set('');
  }

  private cancelRequests(): void {
    this.request?.unsubscribe();
    this.regenerateRequest?.unsubscribe();
    this.resyncRequest?.unsubscribe();
    this.request = null;
    this.regenerateRequest = null;
    this.resyncRequest = null;
  }
}
